import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { FireIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../lib/format';
import ProvinceMapModalContainer from '../../containers/ProvinceMapModalContainer';

const GET_SELLER_AND_MAP = gql`
  query GetSellerAndMap {
    sellerValue
    sumByProvinceOptimized
    countryForCompany
  }
`;

const SellerHeatMap = () => {
  const intl = useIntl();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const openMapModal = () => setIsMapModalOpen(true);
  const closeMapModal = () => setIsMapModalOpen(false);

  const { data, loading, error } = useQuery(GET_SELLER_AND_MAP, { fetchPolicy: 'network-only' });

  const topFive = useMemo(() => {
    const entries = Object.entries(data?.sumByProvinceOptimized || {});
    const sorted = entries.sort((a, b) => (b[1]?.assets_under_management || 0) - (a[1]?.assets_under_management || 0));
    return sorted.slice(0, 5);
  }, [data]);

  const areaLabel = useMemo(() => {
    const c = (data?.countryForCompany || '').toString().toLowerCase();
    if (c.includes('canada')) return intl.formatMessage({ id: 'succession.growth.seller_heat_map.provinces' });
    if (c.includes('us') || c.includes('united states') || c.includes('usa')) return intl.formatMessage({ id: 'succession.growth.seller_heat_map.states' });
    return intl.formatMessage({ id: 'succession.growth.seller_heat_map.states' });
  }, [data, intl]);

  const totals = useMemo(() => {
    const vals = Object.values(data?.sumByProvinceOptimized || {});
    return vals.reduce((acc, v) => {
      acc.aum += v?.assets_under_management || 0;
      acc.gp += v?.gross_production || 0;
      return acc;
    }, { aum: 0, gp: 0 });
  }, [data]);

  const CardShell = ({ children }) => (
    <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
      <div className="px-4 py-5 sm:p-6 h-full flex flex-col">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <CardShell>
        <div className="flex items-center">
          <div className="ml-0">
            <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.title' })}</p>
            <p className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.loading' })}</p>
          </div>
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell>
        <div className="flex items-center">
          <div className="ml-0">
            <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.title' })}</p>
            <p className="text-lg font-semibold text-succession-error transition-colors duration-300">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.error' })}</p>
          </div>
        </div>
      </CardShell>
    );
  }

  return (
    <>
      <CardShell>
        <div className="flex items-center">
          <div className="ml-0 flex-1">
            <h3>{intl.formatMessage({ id: 'succession.growth.seller_heat_map.top_five_heading' }, { areaLabel })}</h3>
          </div>
        </div>

        <div className="mt-4">
          <div className="space-y-2">
            {topFive.map(([name, vals]) => {
              const aum = vals?.assets_under_management || 0;
              const maxAum = topFive.length ? Math.max(...topFive.map(([, v]) => v?.assets_under_management || 0)) : 1;
              const pct = Math.max(6, Math.round((aum / (maxAum || 1)) * 100));
              return (
                <div key={name} className="w-full">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-succession-text-primary truncate pr-2" title={name}>{name}</span>
                    <span className="text-succession-text-secondary">{formatCurrency(aum)}</span>
                  </div>
                  <div className="h-2 bg-succession-bg-secondary rounded">
                    <div className="h-2 bg-succession-primary rounded" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals moved above footer */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.total_aum' })}
            <div className="text-succession-text-primary font-semibold">{formatCurrency(totals.aum)}</div>
          </div>
          <div className="text-right text-succession-text-secondary">{intl.formatMessage({ id: 'succession.growth.seller_heat_map.total_t12' })}
            <div className="text-succession-text-primary font-semibold">{formatCurrency(totals.gp)}</div>
          </div>
        </div>

        <div className="mt-3 pt-2 flex justify-center mt-auto">
          <button
            onClick={openMapModal}
            className="flex items-center space-x-1 hover:opacity-80 transition-opacity duration-300 text-succession-primary text-sm"
            title={intl.formatMessage({ id: 'succession.growth.seller_heat_map.view_map' })}
          >
            <FireIcon className="h-4 w-4 text-succession-warning" aria-hidden="true" />
            <span>{intl.formatMessage({ id: 'succession.growth.seller_heat_map.view_map' })}</span>
          </button>
        </div>
      </CardShell>
      <ProvinceMapModalContainer isOpen={isMapModalOpen} onClose={closeMapModal} />
    </>
  );
};

export default SellerHeatMap;


